import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { toast } from 'react-toastify'
import { useForm, FormProvider } from 'react-hook-form'
import { Box, Button, DialogContent } from '@mui/material'

import noop from 'lodash/noop'
import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import { useLazyGetInvoiceGroupListQuery } from 'api/groups/invoice/getInvoiceGroupList'
import { useAddInvoicesToGroupMutation } from 'api/groups/invoice/addInvoicesToGroup'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import HHFormTextField from 'components/form-fields/v5/HHFormTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import AddEditInvoiceGroupDialog from './AddEditInvoiceGroupDialog'

const AddToGroupDialog = ({ isOpen = false, invoiceIds = [], onClose, onSuccess = noop }) => {
  const [isOpenAddEditInvoiceGroupDialog, setIsOpenAddEditInvoiceGroupDialog] = useState(false)
  const [getInvoiceGroupList, { data: invoiceGroupList = [] }] = useLazyGetInvoiceGroupListQuery()
  const [addInvoicesToGroup, { isLoading: isAddToGroupLoading }] = useAddInvoicesToGroupMutation()
  const addToGroupForm = useForm({ defaultValues: { invoiceGroupId: '' } })
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = addToGroupForm

  const invoiceGroupIdHelperText = get(errors, 'invoiceGroupId.message')
  const sortedGroups = sortByDateSelector({ data: invoiceGroupList, key: 'createdAt' })

  const handleCreateNewGroup = () => setIsOpenAddEditInvoiceGroupDialog(true)

  const handleCreateNewGroupSuccess = id => setValue('invoiceGroupId', id)

  const handleCloseCreateNewGroup = () => setIsOpenAddEditInvoiceGroupDialog(false)

  const onSubmitHandler = data => {
    const payload = { invoiceGroupId: data?.invoiceGroupId, invoiceIds }
    addInvoicesToGroup(payload)
      .unwrap()
      .then(() => {
        toast.success(invoiceIds.length === 1 ? T.ADD_INVOICE_TO_GROUP_SUCCESS_MSG : T.ADD_INVOICES_TO_GROUP_SUCCESS_MSG)
        try {
          onSuccess()
        } finally {
          onClose()
        }
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (isOpen) {
      reset()
      getInvoiceGroupList()
    }
  }, [isOpen])

  return (
    <>
      <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
        <HHDialogTitle title={T.ADD_TO_GROUP} onClose={onClose} />
        <DialogContent>
          <FormProvider {...addToGroupForm}>
            <Box mt={2}>
              <HHFormTextField
                error={invoiceGroupIdHelperText}
                helperText={invoiceGroupIdHelperText}
                control={control}
                required
                rules={{ required: T.CANNOT_BE_EMPTY }}
                name="invoiceGroupId"
                label={T.INVOICE_GROUPS}
                placeholder={T.SELECT_GROUP}
                fullWidth
                deprecatedLabel={false}
                select
              >
                {sortedGroups.map(({ id, groupName }) => (
                  <SelectItem value={id}>{groupName}</SelectItem>
                ))}
              </HHFormTextField>
            </Box>
          </FormProvider>
        </DialogContent>
        <HHDialogActions>
          <CancelButton size="medium" onClick={onClose} />
          <Box sx={{ flex: '1 1 auto' }} />
          <Button size="small" variant="outlined" onClick={handleCreateNewGroup}>
            {T.CREATE_NEW_GROUP}
          </Button>
          <Button loading={isAddToGroupLoading} size="small" variant="contained" onClick={handleSubmit(onSubmitHandler)}>
            {T.ADD}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
      <AddEditInvoiceGroupDialog
        isOpen={isOpenAddEditInvoiceGroupDialog}
        onSuccess={handleCreateNewGroupSuccess}
        onClose={handleCloseCreateNewGroup}
      />
    </>
  )
}

AddToGroupDialog.propTypes = {
  isOpen: PropTypes.bool,
  invoiceIds: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

export default AddToGroupDialog
